import { TreeList, Button } from '@ibsys/kit';
import { DragDropProvider, DragOverlay, type DragEndEvent, PointerSensor } from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';
import { useSortable, isSortable } from '@dnd-kit/react/sortable';
import { RestrictToVerticalAxis } from '@dnd-kit/abstract/modifiers';
import { GripVerticalIcon, FolderIcon, FileTextIcon, PlusIcon, ArrowRightIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import type { CompetencyResponse, ReorderItem } from '@/entities/coaching';
import { LeafNode } from './LeafNode';

type RootNodeProps = {
  competency: CompetencyResponse;
  /** Порядковый номер в списке корневых компетенций */
  index: number;
  onSelect: (id: number) => void;
  onAddChild: (parentId: number) => void;
  onReorder: (parentId: number, items: ReorderItem[]) => void;
  isDesktop?: boolean;
};

export const RootNode: FC<RootNodeProps> = ({ competency, index, onSelect, onAddChild, onReorder, isDesktop }) => {
  const { t } = useTranslation('coaching');

  const children = useMemo(
    () => competency.children.filter((c) => !c.isDeleted).sort((a, b) => a.orderPosition - b.orderPosition),
    [competency.children],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return;
    const { source } = event.operation;
    if (!isSortable(source)) return;
    const { initialIndex, index: newIndex } = source;
    if (initialIndex === newIndex) return;

    const reordered = [...children];
    const [moved] = reordered.splice(initialIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const items: ReorderItem[] = reordered.map((c, idx) => ({
      id: c.id,
      parentCompetencyId: competency.id,
      orderPosition: idx,
    }));

    onReorder(competency.id, items);
  };

  /* На десктопе (контейнер >= 1280px) клик по заголовку группы открывает карточку.
     На мобильном клик только раскрывает аккордеон — навигация через rightSlot-кнопку. */
  const handleRowClick = () => {
    if (isDesktop) {
      onSelect(competency.id);
    }
  };

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onAddChild(competency.id);
  };

  const sortableId = `root-competency-${competency.id}`;
  const { ref, handleRef, isDragging } = useSortable({ id: sortableId, index });

  return (
    <div ref={ref} className={cn(isDragging && 'opacity-50')}>
      <TreeList.Group
        value={String(competency.id)}
        classNames={{
          header: 'group/root',
        }}
        label={
          <div className="flex items-center gap-2 w-full">
            <FolderIcon size={14} className="shrink-0 text-muted-foreground" />
            <span className="line-clamp-2 flex-1 text-left text-sm font-medium leading-tight">{competency.name}</span>
          </div>
        }
        onClick={handleRowClick}
        rightSlot={
          <div className="flex items-center gap-1">
            {/* Кнопка перехода к карточке — только когда контейнер не десктопный */}
            {!isDesktop && (
              <Button
                aria-label={t('openCard')}
                variant="ghost"
                size="icon"
                className="size-6 shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(competency.id);
                }}
              >
                <ArrowRightIcon className="size-3.5" />
              </Button>
            )}
            {/* Кнопки управления — только на десктопе, появляются по hover */}
            {isDesktop && (
              <>
                <Button
                  aria-label={t('addSubCompetency')}
                  variant="ghost"
                  size="icon"
                  className="size-6 shrink-0 opacity-0 transition-opacity group-hover/root:opacity-100"
                  onClick={handleAdd}
                >
                  <PlusIcon className="size-3.5" />
                </Button>
                {/* Перетаскивание */}
                <div
                  className="shrink-0 cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover/root:opacity-100"
                  ref={(el) => handleRef(el)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GripVerticalIcon size={14} />
                </div>
              </>
            )}
          </div>
        }
      >
        {children.length > 0 ? (
          <DragDropProvider
            sensors={[PointerSensor.configure({ activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })] })]}
            modifiers={[RestrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <div className="space-y-0.5">
              {children.map((child, childIndex) => (
                <LeafNode key={child.id} competency={child} index={childIndex} onSelect={onSelect} />
              ))}
            </div>
            <DragOverlay>
              {(source) => {
                const id = Number(String(source.id).replace('competency-', ''));
                const child = children.find((c) => c.id === id);
                if (!child) return null;
                return (
                  <div className="flex items-center gap-2 rounded-lg bg-card px-3 py-2 shadow-lg ring-1 ring-border">
                    <FileTextIcon size={14} className="shrink-0 text-muted-foreground" />
                    <span className="text-sm font-medium">{child.name}</span>
                    <GripVerticalIcon size={14} className="shrink-0 text-muted-foreground ml-auto" />
                  </div>
                );
              }}
            </DragOverlay>
          </DragDropProvider>
        ) : (
          <div className="ml-6 py-2 px-3 text-sm text-muted-foreground">{t('noCompetencies')}</div>
        )}
      </TreeList.Group>
    </div>
  );
};
